import { ReactNode, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, ChevronLeft } from 'lucide-react'
import { getAdjacentPages } from '@/lib/navigation'
import { useLocale, TranslationKey } from '@/i18n'

interface DocPageProps {
  title: string
  description?: string
  children: ReactNode
  keyTakeaways?: string[]
}

export function DocPage({ title, description, children, keyTakeaways }: DocPageProps) {
  const location = useLocation()
  const { locale, t } = useLocale()
  const { prev, next } = getAdjacentPages(location.pathname, locale)

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [location.pathname])

  return (
    <>
      <article className="flex-1 min-w-0 py-6 md:py-8 px-4 md:px-6 lg:px-12 max-w-[900px] flex flex-col" role="article" aria-labelledby="page-title">
        {/* Breadcrumb */}
        <Breadcrumb />

        {/* Header */}
        <header className="mb-8 md:mb-10">
          <h1 id="page-title" className="text-[26px] md:text-[32px] font-semibold text-[#FAFAFA] mb-3 md:mb-4 leading-[1.2] tracking-[-0.02em]">{title}</h1>
          {description && (
            <p className="text-[15px] md:text-[17px] text-[#D4D4D8] leading-[1.7]">{description}</p>
          )}
        </header>

        {/* Main content */}
        <div className="doc-content">
          {children}
        </div>

        {/* Key takeaways */}
        {keyTakeaways && keyTakeaways.length > 0 && (
          <div className="mt-8 md:mt-12 p-5 md:p-6 bg-[rgba(252,135,183,0.04)] border border-[rgba(252,135,183,0.15)] rounded-xl md:rounded-2xl">
            <h3 className="text-[14px] md:text-[15px] font-semibold text-[#FAFAFA] mb-3 md:mb-4 flex items-center gap-2">
              <span className="w-5 h-5 rounded-md bg-[rgba(252,135,183,0.2)] flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-[#FC87B7]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              {t('doc.keyTakeaways')}
            </h3>
            <ul className="space-y-2.5 md:space-y-3">
              {keyTakeaways.map((takeaway, index) => (
                <li key={index} className="flex items-start gap-2.5 md:gap-3 text-[14px] md:text-[15px] text-[#E4E4E7] leading-[1.6]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FC87B7] mt-[0.4rem] md:mt-2 flex-shrink-0" />
                  <span>{takeaway}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Navigation */}
        {(prev || next) && (
          <nav className="mt-8 md:mt-12 pt-6 md:pt-8 border-t border-[rgba(255,255,255,0.06)] grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {prev ? (
              <Link
                to={prev.href}
                className="group relative flex flex-col p-4 md:p-5 rounded-xl md:rounded-2xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.06)] active:scale-[0.98] md:hover:border-[rgba(252,135,183,0.4)] md:hover:bg-[rgba(255,255,255,0.04)] transition-all duration-300 ease-out md:hover:shadow-[0_0_30px_rgba(252,135,183,0.08)] overflow-hidden"
              >
                <span className="text-[10px] md:text-[11px] font-medium uppercase tracking-wider text-[#71717A] mb-1.5 md:mb-2">
                  {t('doc.previous')}
                </span>
                <span className="flex items-center gap-2 w-full min-w-0">
                  <span className="flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-full bg-[rgba(255,255,255,0.04)] group-hover:bg-[rgba(252,135,183,0.15)] transition-all duration-300 flex-shrink-0">
                    <ChevronLeft className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#71717A] group-hover:text-[#FC87B7] transition-colors duration-300" />
                  </span>
                  <span className="text-[14px] md:text-[15px] font-medium text-[#FAFAFA] group-hover:text-[#FC87B7] transition-colors duration-300 truncate min-w-0">
                    {prev.title}
                  </span>
                </span>
              </Link>
            ) : (
              <div className="hidden md:block" />
            )}
            {next ? (
              <Link
                to={next.href}
                className="group relative flex flex-col items-end p-4 md:p-5 rounded-xl md:rounded-2xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.06)] active:scale-[0.98] md:hover:border-[rgba(252,135,183,0.4)] md:hover:bg-[rgba(255,255,255,0.04)] transition-all duration-300 ease-out md:hover:shadow-[0_0_30px_rgba(252,135,183,0.08)] overflow-hidden"
              >
                <span className="text-[10px] md:text-[11px] font-medium uppercase tracking-wider text-[#71717A] mb-1.5 md:mb-2">
                  {t('doc.next')}
                </span>
                <span className="flex items-center gap-2 w-full min-w-0 justify-end">
                  <span className="text-[14px] md:text-[15px] font-medium text-[#FAFAFA] group-hover:text-[#FC87B7] transition-colors duration-300 truncate min-w-0 text-right">
                    {next.title}
                  </span>
                  <span className="flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-full bg-[rgba(255,255,255,0.04)] group-hover:bg-[rgba(252,135,183,0.15)] transition-all duration-300 flex-shrink-0">
                    <ChevronRight className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#71717A] group-hover:text-[#FC87B7] transition-colors duration-300" />
                  </span>
                </span>
              </Link>
            ) : (
              <div className="hidden md:block" />
            )}
          </nav>
        )}
      </article>
    </>
  )
}

// Map section slugs to the first page in that section
const sectionFirstPage: Record<string, string> = {
  'vision-product-market': '/faq/vision-product-market/what-is-rexhook',
  'technical': '/faq/technical/v4-hooks',
  'team-trust': '/faq/team-trust/overview',
  'revenue': '/faq/revenue/how-makes-money',
  'participation': '/faq/participation/go-to-market',
}

// Map section slugs to translation key for breadcrumb label
const sectionLabelKey: Record<string, TranslationKey> = {
  'vision-product-market': 'section.vision-product-market',
  'technical': 'section.technical',
  'team-trust': 'section.team-trust',
  'revenue': 'section.revenue',
  'participation': 'section.participation',
}

function Breadcrumb() {
  const location = useLocation()
  const { locale, t, localizeHref, stripLocale } = useLocale()
  const strippedPath = stripLocale(location.pathname)
  const pathParts = strippedPath.split('/').filter(Boolean)

  const breadcrumbItems = pathParts.map((part, index) => {
    const path = '/' + pathParts.slice(0, index + 1).join('/')

    // Use translated section label when matched, else titlecased slug
    const translatedLabel = sectionLabelKey[part] ? t(sectionLabelKey[part]) : null
    const label = translatedLabel || part
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')

    // Resolve the link: /faq → home, /faq/<section> → first page of section
    let resolvedPath = path
    if (path === '/faq') {
      resolvedPath = '/'
    } else if (index === 1 && pathParts[0] === 'faq' && sectionFirstPage[part]) {
      resolvedPath = sectionFirstPage[part]
    }

    return { label, path: localizeHref(resolvedPath), isLast: index === pathParts.length - 1 }
  })

  // Suppress unused-locale warning while keeping the dep for re-renders
  void locale

  return (
    <nav className="flex items-center gap-1.5 md:gap-2 text-[12px] md:text-[13px] text-[#A1A1AA] mb-6 md:mb-8 flex-wrap overflow-x-auto pb-1 -mx-1 px-1">
      <Link to={localizeHref('/')} className="hover:text-[#FAFAFA] transition-colors duration-200 flex-shrink-0">
        {t('doc.home')}
      </Link>
      {breadcrumbItems.map((item, index) => (
        <span key={index} className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
          <span className="text-[#71717A]">/</span>
          {item.isLast ? (
            <span className="text-[#D4D4D8] truncate max-w-[150px] md:max-w-none">{item.label}</span>
          ) : (
            <Link to={item.path} className="hover:text-[#FAFAFA] transition-colors duration-200">
              {item.label}
            </Link>
          )}
        </span>
      ))}
    </nav>
  )
}
